<template>
	<div class="avatar display-flex align-center"
			:style="`background-image:url(${avatarUrl});`"
			:class="[size, bgColor, {'border': border}]">
		<div
			class="avatar display-flex align-center"
			alt=""
		/>
		<span
			v-if="!plus"
			class="initials display-flex justify-content-center col bold"
			:class="[initialsColor]"
			>{{ nameInitials }}</span
		>
		<span
			v-else
			class="initials display-flex justify-content-center ink-lighter-1"
			>+{{ plus }}</span
		>
	</div>
</template>

<script>
export default {
	name: 'b-avatar',
	props: {
		/**
		 * Url da imagem que será usada como `background` do avatar
		 */
		avatarUrl: String,
		/**
		 * Cor que será aplicada ao fundo e as letras do avatar, caso a prop `avatarUrl` não seja passada. A cor 'blue' é aplicada como padrão
		 */
		color: {
			type: String
		},
		/**
		 * Nome do qual são extraídas as iniciais que serão exibidas no avatar, caso a prop `avatarUrl` não seja passada
		 */
		name: String,
		/**
		 * Se true o avatar tem borda cinza ao redor
		 */
		border: {
			type: Boolean
		},
		/**
		 * Se true o avatar mostra '+' e o número fornecido
		 */
		plus: {
			type: Number
		},
		/**
		 * Se true o avatar tem tamanho xtiny. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		xtiny: Boolean,
		/**
		 * Se true o avatar tem tamanho tiny. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		tiny: Boolean,
		/**
		 * Se true o avatar tem tamanho normal. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		normal: Boolean,
		/**
		 * Se true o avatar tem tamanho big. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		big: Boolean,
		/**
		 * Se true o avatar tem tamanho xbig. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		xbig: Boolean,
		/**
		 * Se true o avatar tem tamanho huge. Caso nenhuma prop correspondente ao tamanho seja passada, é aplicado tamanho 'normal'
		 */
		huge: Boolean
	},
	computed: {
		size() {
			if (this.xtiny) return 'xtiny';
			if (this.tiny) return 'tiny';
			if (this.big) return 'big';
			if (this.xbig) return 'xbig';
			if (this.huge) return 'huge';
			return 'normal';
		},
		bgColor() {
			if (
				this.color === 'blue' ||
				this.color === 'green' ||
				this.color === 'red' ||
				this.color === 'yellow' ||
				this.color === 'purple' ||
				this.color === 'pink' ||
				this.color === 'teal'
			)
				return 'bg-' + this.color + '-lighter-3';
			else return 'bg-' + this.randomColor + '-lighter-3';
		},
		initialsColor() {
			if (
				this.color === 'blue' ||
				this.color === 'green' ||
				this.color === 'red' ||
				this.color === 'yellow' ||
				this.color === 'purple' ||
				this.color === 'pink' ||
				this.color === 'teal'
			)
				return this.color + '-base';
			else return this.randomColor + '-base';
		},
		randomColor() {
			const colors = ['blue', 'green', 'red', 'yellow', 'purple', 'pink', 'teal'];
			return colors[Math.floor(Math.random() * colors.length)];
		},
		nameInitials() {
			if (!this.name || this.avatarUrl) return;
			const nameArray = this.name !== '' && this.name !== undefined ? this.name.split(' ') : '?';
			const firstLetter = nameArray[0].slice(0, 1);
			const secondLetter = nameArray.length > 1 ? nameArray[nameArray.length - 1].slice(0, 1) : '';
			return (firstLetter + secondLetter).toUpperCase();
		}
	}
};
</script>

<style lang="scss" scoped>
.avatar {
	width: 100%;
	border-radius: 100%;
	background-size: cover;
	background-position: center center;
	flex-shrink: 0;
	display: block;

	> div {
		z-index: 2;
	}
	&.border {
		border: 1px solid #dcdbe1;
		
		> span {
			padding: 0 4px 2px 0;
		}
	}
	.initials {
		z-index: 1;
	}
	.border-spacing {
		margin: -2px;
	}
	&.xtiny {
		width: 3.2rem;
		height: 3.2rem;
		.initials {
			width: 3.2rem;
			height: 3.2rem;
			font-size: 1.313rem;
			align-items: center;
			line-height: 1;
		}
	}
	&.tiny {
		width: 3.6rem;
		height: 3.6rem;
		.initials {
			width: 3.6rem;
			height: 3.6rem;
			font-size: 1.8rem;
			align-items: center;
			line-height: 1;
		}
	}
	&.normal {
		width: 4rem;
		height: 4rem;
		.initials {
			width: 4rem;
			height: 4rem;
			font-size: 2rem;
			align-items: center;
			line-height: 1;
		}
	}
	&.big {
		width: 6.4rem;
		height: 6.4rem;
		.initials {
			width: 6.4rem;
			height: 6.4rem;
			font-size: 3.2rem;
			align-items: center;
			line-height: 1;
		}
	}
	&.xbig {
		width: 12.8rem;
		height: 12.8rem;
		.initials {
			width: 12.8rem;
			height: 12.8rem;
			font-size: 6.4rem;
			align-items: center;
			line-height: 1;
		}
	}
	&.huge {
		width: 25.6rem;
		height: 25.6rem;
		.initials {
			width: 25.6rem;
			height: 25.6rem;
			font-size: 12.8rem;
			align-items: center;
			line-height: 1;
		}
	}
}
</style>

<docs>
    Avatar

    As importações dos arquivos abaixo são necessárias para o funcionamento do componente:
    - grid.scss
    - position.scss
    - styling.scss
    - typography.scss

    ```js
    <b-avatar xtiny name="maria da silva" style="margin-bottom:4px;"/>
    <b-avatar tiny name="Maria da Silva" style="margin-bottom:4px;"/>
    <b-avatar normal name="Maria da Silva" style="margin-bottom:4px;"/>
    <b-avatar big name="Maria da Silva" style="margin-bottom:4px;"/>
    <b-avatar xbig name="Maria da Silva" style="margin-bottom:4px;"/>
    <b-avatar huge name="Maria" color="pink" style="margin-bottom:4px;"/>
    <b-avatar big avatarUrl="https://media.licdn.com/dms/image/C4D0BAQFhFfw3LHikuA/company-logo_200_200/0?e=2159024400&v=beta&t=7QuonSJwCtamBh4CMnAjljlgfgANgM_6nwBOkCvjtHQ" style="margin-bottom:4px;"/>
    ```
</docs>
